import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  LinkIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { feedbackAPI } from '../../services/api';

const STATUSES = ['all', 'new', 'reviewed', 'contacted', 'closed'];
const STATUS_STYLE = {
  new: 'bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-300',
  reviewed: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300',
  contacted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
  closed: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};
const INTENT_LABEL = {
  capture_leads: 'Capture more leads',
  compare_options: 'Compare AI-agent options',
  pricing: 'Understand pricing',
  exploring: 'Explore Serviti',
  other: 'Something else',
};
const FOUND_LABEL = { yes: 'Found it', partly: 'Partly', no: 'Did not find it' };
const PAGE_SIZE = 50;

const dateLabel = (value) => {
  if (!value) return 'Unknown time';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(new Date(value));
};

const WebsiteFeedbackPage = () => {
  const [status, setStatus] = useState('new');
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await feedbackAPI.list({
        ...(status === 'all' ? {} : { status }),
        limit: PAGE_SIZE,
        offset,
      });
      setData(response.data || response);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || 'Could not load website feedback.');
    } finally {
      setLoading(false);
    }
  }, [offset, status]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (item, nextStatus) => {
    if (nextStatus === item.status || updating[item.id]) return;
    setUpdating((current) => ({ ...current, [item.id]: true }));
    setError(null);
    try {
      await feedbackAPI.updateStatus(item.id, nextStatus);
      await load();
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || 'Could not update this feedback.');
    } finally {
      setUpdating((current) => ({ ...current, [item.id]: false }));
    }
  };

  const totalAll = Object.values(data.counts || {}).reduce((sum, count) => sum + Number(count || 0), 0);

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-orange-600 dark:text-orange-400">
            Voice of the visitor
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-gray-950 dark:text-white">
            Website Feedback
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
            Read what visitors were trying to do, where the website fell short, and who asked for a follow-up.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      {error && (
        <div role="alert" className="flex items-start justify-between gap-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          <span>{error}</span>
          <button type="button" className="font-semibold underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-5" aria-label="Feedback status filters">
        {STATUSES.map((candidate) => {
          const count = candidate === 'all' ? totalAll : Number(data.counts?.[candidate] || 0);
          const selected = status === candidate;
          return (
            <button
              type="button"
              key={candidate}
              aria-pressed={selected}
              onClick={() => { setStatus(candidate); setOffset(0); }}
              className={`rounded-2xl border p-4 text-left transition ${selected
                ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-200 dark:border-orange-500 dark:bg-orange-950/30 dark:ring-orange-900'
                : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'}`}
            >
              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {candidate}
              </span>
              <span className="mt-1 block text-2xl font-bold text-gray-950 dark:text-white">{count}</span>
            </button>
          );
        })}
      </section>

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
          Loading feedback…
        </div>
      ) : data.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
          <ChatBubbleLeftRightIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
          <h2 className="mt-3 font-semibold text-gray-900 dark:text-white">No {status === 'all' ? '' : status} feedback yet</h2>
          <p className="mt-1 text-sm text-gray-500">New website responses will appear here.</p>
        </div>
      ) : (
        <section className="space-y-4" aria-label="Feedback submissions">
          {data.items.map((item) => (
            <article key={item.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex flex-col gap-4 border-b border-gray-100 p-5 sm:flex-row sm:items-start sm:justify-between dark:border-gray-700">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLE[item.status] || STATUS_STYLE.closed}`}>
                      {item.status}
                    </span>
                    {item.follow_up_requested && (
                      <span className="rounded-full bg-fuchsia-100 px-2.5 py-1 text-xs font-semibold text-fuchsia-800 dark:bg-fuchsia-950/50 dark:text-fuchsia-300">
                        Follow-up requested
                      </span>
                    )}
                  </div>
                  <h2 className="mt-3 text-lg font-semibold text-gray-950 dark:text-white">
                    {INTENT_LABEL[item.intent] || 'Visitor feedback'}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {FOUND_LABEL[item.found_answer] || 'Question not answered'} · {dateLabel(item.created_at)}
                  </p>
                </div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Status
                  <select
                    value={item.status}
                    disabled={Boolean(updating[item.id])}
                    onChange={(event) => changeStatus(item, event.target.value)}
                    aria-label={`Status for feedback ${item.id}`}
                    className="mt-1 block min-w-[150px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium normal-case text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  >
                    {STATUSES.slice(1).map((candidate) => <option key={candidate}>{candidate}</option>)}
                  </select>
                </label>
              </div>

              <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div>
                  <p className="whitespace-pre-wrap text-[15px] leading-7 text-gray-800 dark:text-gray-200">
                    {item.comment || <span className="italic text-gray-400">No written comment.</span>}
                  </p>
                </div>
                <aside className="space-y-2 rounded-xl bg-gray-50 p-4 text-sm dark:bg-gray-900/70">
                  {item.contact && (
                    <a
                      href={item.contact.includes('@') ? `mailto:${item.contact}` : `tel:${item.contact}`}
                      className="flex items-center gap-2 break-all font-semibold text-blue-700 hover:underline dark:text-blue-300"
                    >
                      {item.contact.includes('@') ? <EnvelopeIcon className="h-4 w-4 shrink-0" /> : <PhoneIcon className="h-4 w-4 shrink-0" />}
                      {item.contact}
                    </a>
                  )}
                  <div className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                    <LinkIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="break-all">{item.page_path || '/'}</span>
                  </div>
                  {(item.utm_source || item.utm_campaign) && (
                    <p className="text-xs text-gray-500">
                      UTM: {[item.utm_source, item.utm_medium, item.utm_campaign].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  <div className="flex items-center gap-2 pt-2 text-xs text-gray-500">
                    <CheckCircleIcon className={`h-4 w-4 ${item.email_notified ? 'text-emerald-500' : 'text-gray-300'}`} />
                    Email {item.email_notified ? 'sent' : 'not sent'}
                    {item.follow_up_requested && <> · WhatsApp {item.whatsapp_notified ? 'sent' : 'not sent'}</>}
                  </div>
                  {item.notification_error && <p className="text-xs text-amber-700 dark:text-amber-400">{item.notification_error}</p>}
                </aside>
              </div>
            </article>
          ))}
          <nav className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800" aria-label="Feedback pages">
            <span className="text-gray-500 dark:text-gray-400">
              Showing {offset + 1}–{Math.min(offset + data.items.length, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={offset + data.items.length >= data.total}
                onClick={() => setOffset((current) => current + PAGE_SIZE)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200"
              >
                Next
              </button>
            </div>
          </nav>
        </section>
      )}
    </div>
  );
};

export default WebsiteFeedbackPage;
